import type { Store } from "vuex";
import type { useFeaturesStore } from "@/stores/features";

export enum Section {
    Overview,
    Access,
    Licenses,
    Modules,
    Attributes,
    Misc,
    Customer,
}

type Features = ReturnType<typeof useFeaturesStore>;

// What the access store answers, narrowed so tests need not build one.
type Access = { canBeSet: boolean };

export const sections = [
    Section.Overview,
    Section.Access,
    Section.Licenses,
    Section.Modules,
    Section.Attributes,
    Section.Misc,
    Section.Customer,
];

// Stable, human readable names for use in links. Do not rename these, old
// links will stop working.
const slugs: Record<Section, string> = {
    [Section.Overview]: "overview",
    [Section.Access]: "access",
    [Section.Licenses]: "licenses",
    [Section.Modules]: "modules",
    [Section.Attributes]: "attributes",
    [Section.Misc]: "misc",
    [Section.Customer]: "customer",
};

export const sectionToSlug = (section: Section): string => slugs[section];

export const sectionFromSlug = (slug: string | null): Section | null => {
    if (!slug) {
        return null;
    }

    const match = sections.find((section) => slugs[section] === slug);

    return match === undefined ? null : match;
};

export const isSectionVisible = (
    section: Section,
    store: Store<any>,
    features: Features,
    access: Access
): boolean => {
    switch (section) {
        case Section.Overview:
            return true;
        case Section.Access:
            return features.access && access.canBeSet;
        case Section.Licenses:
            return (
                features.licenses &&
                (store.getters["licenses/typeCanBeSet"]("license") ||
                    store.getters["licenses/typeCanBeSet"]("licenseTeaser") ||
                    store.getters["licenses/typeCanBeSet"]("licenseLogin"))
            );
        case Section.Attributes:
            return features.attributes;
        case Section.Modules:
            return features.modules;
        case Section.Misc:
            return features.misc;
        case Section.Customer:
            return features.customer;
        default:
            return false;
    }
};
